/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Snackbar from "@mui/material/Snackbar";
import Icon from "@mui/material/Icon";
import Fade from "@mui/material/Fade";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Custom styles for the MDSnackbar
import MDSnackbarIconRoot from "components/MDSnackbar/MDSnackbarIconRoot";

// Material Dashboard 2 React context
import { useMaterialUIController } from "context";
import MDButton from "components/MDButton";
import { useNavigate } from "react-router-dom";

function MDSnackbar({ color="info", icon = "", content, close, bgWhite=false, button, ...rest }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const navigate = useNavigate();


  let titleColor;

    if (bgWhite) {
    titleColor = color;
  } else if (color === "light") {
    titleColor = darkMode ? "inherit" : "dark";
  } else {
    titleColor = "white";
  }

  const renderIcon = (
      <MDSnackbarIconRoot fontSize="small" ownerState={{ color, bgWhite }}>
        {icon}
      </MDSnackbarIconRoot>
    );

  const renderButton =
    button && button.name && button.link ? (
      <MDButton
        // color="inherit"
        size="small"
        onClick={() => navigate(button.link)}
        variant="button"
        fontWeight="regular"
        textGradient={bgWhite}
        sx={{
          color: ({ palette: { dark, white } }) =>
            (bgWhite && !darkMode) || color === "light" ? dark.main : white.main,
          marginLeft: 2,
          transform: "translateY(-1px)",
        }}
      >
        {button.name}
      </MDButton>
    ) : null;

  return (
    <Snackbar
      TransitionComponent={Fade}
      autoHideDuration={1000}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      {...rest}
    >
      <MDBox
        variant={bgWhite ? "contained" : "gradient"}
        bgColor={bgWhite ? "white" : color}
        minWidth="21.875rem"
        maxWidth="100%"
        shadow="md"
        borderRadius="md"
        p={1}
        sx={{
          backgroundColor: ({ palette }) =>
            darkMode ? palette.background.card : palette[color] || palette.white.main,
        }}
      >
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          color="dark"
          p={1.5}
        >
          <MDBox display="flex" alignItems="center" lineHeight={0}>
            {icon !== "" ? (renderIcon) : null}
            <MDTypography
              variant="button"
              fontWeight="regular"
              color={titleColor}
              textGradient={bgWhite}
            >
              {content}
            </MDTypography>
            {renderButton}
          </MDBox>
          <MDBox display="flex" alignItems="center" lineHeight={0}>
            <Icon
              sx={{
                color: ({ palette: { dark, white } }) =>
                  (bgWhite && !darkMode) || color === "light" ? dark.main : white.main,
                fontWeight: ({ typography: { fontWeightBold } }) => fontWeightBold,
                cursor: "pointer",
                marginLeft: 2,
                transform: "translateY(-1px)",
              }}
              onClick={close}
            >
              close
            </Icon>
          </MDBox>
        </MDBox>
      </MDBox>
    </Snackbar>
  );
}

// Typechecking props for MDSnackbar
MDSnackbar.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
  ]),
  icon: PropTypes.node.isRequired,
  content: PropTypes.node.isRequired,
  close: PropTypes.func.isRequired,
  bgWhite: PropTypes.bool,
  button: PropTypes.shape({
    name: PropTypes.string,
    link: PropTypes.string,
  }),
};

export default MDSnackbar;
